/**
 * The one call site that puts Core's agent surface on the document.
 *
 * Called from `main.tsx` at module scope, before React mounts. Not from an
 * effect, not from a component: `installAgentSurface` manages the DOCUMENT's
 * lifetime and React has no lifecycle that matches it. See the header of
 * `install.ts` for why a second registration must be unreachable rather than
 * caught.
 *
 * This module owns nothing but the wiring. The catalog is `catalog.ts`, the
 * executable body of every tool is `runtime.ts`, the capability seams every
 * command runs against are `capabilities.ts`, and the lifecycle is
 * `install.ts`.
 */
import log from '@/lib/logger';
import { buildCapabilities } from './capabilities';
import { CATALOG } from './catalog';
import { installAgentSurface, type InstallOutcome } from './install';
import { resolveModelContext } from './modelContext';
import { installPageToolsForChat } from './pageToolsForChat';
import { createToolBodyBuilder, installCapabilities } from './runtime';

/**
 * Offers the installed read-only tools to Emjae's chat, if any opted in.
 *
 * Driven off `emjaeExposed` on the outcome rather than off the catalog: that
 * field is what actually registered in THIS document, and it is folded into
 * the install fingerprint, so it cannot disagree with the tools that are live.
 *
 * Every other outcome joins nothing, `reused` included — a reuse means another
 * module instance already registered this catalog and its own join is still
 * standing, so doing it again would only replace the seam with an identical
 * one.
 *
 * Never throws. A failure here costs the chat its page tools and costs the
 * page nothing else, which is not worth taking Core's boot down for.
 */
async function joinPageToolsToChat(outcome: InstallOutcome): Promise<void> {
  if (outcome.kind !== 'installed' || outcome.reused) return;
  try {
    const joined = await installPageToolsForChat(outcome.emjaeExposed);
    log.debug('[agent-surface] page tools offered to the chat', { count: joined });
  } catch (error) {
    log.debug('[agent-surface] page tools not offered to the chat', {
      reason: error instanceof Error ? error.name : 'unknown',
    });
  }
}

/**
 * Every terminal state, at debug and no louder.
 *
 * None of these are app faults. An operator on a browser without WebMCP, an
 * HMR cycle that changed the catalog, and an extension that squatted a name
 * are all conditions the page is expected to survive silently, so paging
 * Sentry or spending a warn on them would be noise. Fields are counts, enum
 * members and our own command names; no argument, receipt or service response
 * is reachable from here.
 */
function reportOutcome(outcome: InstallOutcome): void {
  switch (outcome.kind) {
    case 'installed':
      log.debug('[agent-surface] installed', {
        toolCount: outcome.toolCount,
        reused: outcome.reused,
      });
      return;

    case 'unavailable':
      log.debug('[agent-surface] not installed', { because: outcome.because });
      return;

    case 'stale-catalog':
      log.debug(
        '[agent-surface] a different catalog is already registered for this document; ' +
          'the previously registered tools are still serving. Reload the page.',
      );
      return;

    case 'foreign-collision':
      // `incumbents` is deliberately not logged: it is a foreign registrant's
      // own title and description, which is exactly the kind of untrusted
      // payload this surface never puts in our logs.
      log.debug(
        '[agent-surface] tool names are owned by a registrant we do not control; ' +
          'not installed. Reload the page.',
        { names: outcome.names },
      );
      return;

    case 'failed':
      // Count only. A `ToolFailure.message` can embed a service response.
      log.debug('[agent-surface] install failed', {
        phase: outcome.phase,
        count: outcome.failures.length,
      });
      return;

    default: {
      const unreachable: never = outcome;
      log.debug('[agent-surface] unrecognized install outcome', {
        kind: (unreachable as { readonly kind?: unknown }).kind,
      });
    }
  }
}

/**
 * Installs the catalog if this browser has WebMCP, and does nothing otherwise.
 *
 * Never throws and never returns a promise. `installAgentSurface` resolves
 * every failure as an `InstallOutcome` and never rejects, so nothing here can
 * float a rejection into `window.onunhandledrejection` in `main.tsx`.
 */
export function mountAgentSurface(): void {
  if (!resolveModelContext()) {
    log.debug('[agent-surface] no WebMCP model context in this browser; not mounting');
    return;
  }

  // A duplicate command name installs NOTHING. `asCatalog` reports duplicates
  // as a value instead of throwing during module evaluation, precisely so this
  // decision can be made here, after Sentry and the global handlers exist. The
  // whole surface stands down and Core runs; the alternative, a throw in the
  // static import graph, left the operator looking at the loading shell.
  //
  // Error level, alone in this file, because unlike every outcome below it this
  // one is our own catalog being wrong.
  if (CATALOG.duplicates.length > 0) {
    log.error('[agent-surface] duplicate command names in the catalog; no tools installed', {
      duplicates: CATALOG.duplicates,
    });
    return;
  }

  // Capabilities BEFORE the catalog, and the order is load-bearing. Chrome can
  // call a tool the instant it is registered, and a call that lands before this
  // falls into the runtime's boot-window wait and then refuses retryably. That
  // path exists for the genuine race with React mount, not as somewhere to park
  // an ordering mistake we control from here.
  //
  // Install only, no teardown. `uninstallCapabilities` is exported and would be
  // the right thing to run on document destruction, but `install.ts` owns the
  // only `pagehide` listener and exports no hook to join it, so registering a
  // second listener here would duplicate the bfcache reasoning it documents at
  // `install.ts:541` and could diverge from it. Left for the lane that owns
  // that file to expose a teardown seam.
  installCapabilities(buildCapabilities());

  void installAgentSurface(CATALOG.catalog, { buildBody: createToolBodyBuilder() })
    .then((outcome) => {
      reportOutcome(outcome);
      return joinPageToolsToChat(outcome);
    })
    .then(() => void import('./panel/operatorSurface').then((m) => m.mountOperatorSurface(), () => undefined));
}
